import type { AccountVariable } from '@/client/accounts';
import { TextAlignment } from '@/client/courses/types';
import { useToast } from '@/hooks/useToast';
import { setupTooltipHoverForRenderer } from '@/utils/helpers/point-of-contact-tooltip.helper';
import {
  isPhrasingSafeHtml,
  toPhrasingHtml,
} from '@/utils/helpers/string.helper';
import React, {
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

const StyledDiv = styled.div<{ isTitle?: boolean }>`
  padding: 0;
  height: auto;
  overflow: visible;
  p,
  li {
    font-size: var(--medium-font-size);
  }

  ul,
  ol {
    list-style: none;
    padding-inline-start: 0;
  }

  ul li,
  ol li {
    position: relative;
    margin-top: var(--medium-padding);
  }

  ul li:not([class*='ql-indent']),
  ol li:not([class*='ql-indent']) {
    padding-inline-start: var(--medium-padding);
  }

  ul li.ql-indent-1,
  ol li.ql-indent-1 {
    padding-inline-start: calc(var(--medium-padding) * 2.5) !important;
  }

  ul li.ql-indent-2,
  ol li.ql-indent-2 {
    padding-inline-start: calc(var(--medium-padding) * 4) !important;
  }

  ul li.ql-indent-3,
  ol li.ql-indent-3 {
    padding-inline-start: calc(var(--medium-padding) * 5.5) !important;
  }

  ol li:not([class*='ql-indent'])::before {
    margin-inline-end: 12px;
  }

  ul li:not([class*='ql-indent'])::before {
    content: '';
    position: absolute;
    top: -2px;
    inset-inline-start: var(--default-padding);
    width: 10px;
    height: 20px;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='11' height='20' viewBox='0 0 11 20' fill='none'><circle cx='1.5921' cy='12.0921' r='1.0921' fill='%23E94F37' stroke='%23E94F37'/></svg>");
    background-repeat: no-repeat;
    background-size: cover;
  }

  /* 2nd and 3rd level */
  ul li.ql-indent-1::before,
  ul li.ql-indent-2::before {
    content: '';
    position: absolute;
    inset-inline-start: var(--default-padding);
    width: 10px;
    height: 14px;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='11' height='14' viewBox='0 0 11 14' fill='none'><rect x='4.2793' y='7.58984' width='2.79871' height='2.79871' stroke='%235B5950'/></svg>");
    background-repeat: no-repeat;
    background-size: contain;
  }

  /* 4th level */
  ul li.ql-indent-3::before,
  ol li.ql-indent-3::before {
    content: '';
    position: absolute;
    top: -2px;
    inset-inline-start: var(--default-padding);
    width: 10px;
    height: 20px;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='11' height='20' viewBox='0 0 11 20' fill='none'><circle cx='1.5921' cy='12.0921' r='1.0921' fill='%23E94F37' stroke='%23E94F37'/></svg>");
    background-repeat: no-repeat;
    background-size: contain;
  }

  ul[data-checked] li::before,
  ol[data-checked] li::before {
    content: '';
    position: absolute;
    cursor: initial;
    top: 6px;
    inset-inline-start: var(--default-padding);
    width: 12px;
    height: 100%;
    background-image: url("data:image/svg+xml;utf8,<svg width='12' height='9' viewBox='0 0 12 9' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M4.19632 8.85276L0 4.65644L1.04908 3.60736L4.19632 6.7546L10.9509 0L12 1.04908L4.19632 8.85276Z' fill='%23EF4D35'/></svg>");
    background-repeat: no-repeat;
    background-size: contain;
  }

  ul[data-checked] li.ql-indent-1::before,
  ol[data-checked] li.ql-indent-1::before,
  ul[data-checked] li.ql-indent-2::before,
  ol[data-checked] li.ql-indent-2::before,
  ul[data-checked] li.ql-indent-3::before,
  ol[data-checked] li.ql-indent-3::before {
    content: '';
    position: absolute;
    cursor: initial;
    top: 6px;
    inset-inline-start: var(--default-padding);
    width: 12px;
    height: 100%;
    background-image: url("data:image/svg+xml;utf8,<svg width='12' height='9' viewBox='0 0 12 9' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M4.19632 8.85276L0 4.65644L1.04908 3.60736L4.19632 6.7546L10.9509 0L12 1.04908L4.19632 8.85276Z' fill='%23EF4D35'/></svg>");
    background-repeat: no-repeat;
    background-size: contain;
  }

  p {
    * {
      background-color: transparent !important;
    }
  }
  &.text-center ul,
  &.text-center ol {
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;

    li {
      text-align: auto !important;
    }
  }

  &[dir='rtl'] ul li::before,
  &[dir='rtl'] ol li::before {
    inset-inline-start: calc(var(--medium-padding) - var(--default-padding));
    margin-right: 0;
  }

  ${({ isTitle }) =>
    isTitle &&
    css`
      margin: 0;
      min-width: 100px;
      padding-bottom: var(--medium-padding);
      font-size: 30px;
      font-weight: 700;
      p {
        font-size: 30px;
        font-weight: 700;
      }
    `}
`;

export type RichTextRendererProps = {
  content: string;
  className?: string;
  textAlignment?: TextAlignment;
  isTitle?: boolean;
  headingLevel?: 2 | 3;
  tabIndex?: number;
  rtl?: boolean;
  accountVariables?: AccountVariable[];
};

export const RichTextRenderer = React.forwardRef<
  HTMLElement,
  RichTextRendererProps
>(
  (
    {
      content,
      className = '',
      textAlignment = TextAlignment.LEFT,
      isTitle = false,
      headingLevel,
      tabIndex,
      rtl = false,
      accountVariables,
    },
    forwardedRef,
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(
      forwardedRef,
      () => containerRef.current as HTMLElement,
    );

    const headingRank = headingLevel ?? (isTitle ? 2 : undefined);
    const canFlatten = !!headingRank && isPhrasingSafeHtml(content);
    const headingTag = canFlatten ? (`h${headingRank}` as const) : undefined;
    const keepsMarkup = !!headingRank && !canFlatten;
    const toast = useToast();
    const { t } = useTranslation();

    const notifyCopied = useCallback(
      () => toast?.success('', t('clipboard.copied')),
      [toast, t],
    );

    useLayoutEffect(() => {
      const cleanup = setupTooltipHoverForRenderer(
        containerRef,
        rtl,
        accountVariables,
        notifyCopied,
      );
      return cleanup;
    }, [content, rtl, accountVariables, notifyCopied]);

    return (
      <StyledDiv
        as={headingTag ?? 'div'}
        role={keepsMarkup ? 'heading' : undefined}
        aria-level={keepsMarkup ? headingRank : undefined}
        ref={containerRef}
        isTitle={isTitle}
        className={`ql-editor ${className} text-${textAlignment}`}
        dangerouslySetInnerHTML={{
          __html: headingTag ? toPhrasingHtml(content) : content,
        }}
        dir={rtl ? 'rtl' : undefined}
        tabIndex={tabIndex}
      />
    );
  },
);

RichTextRenderer.displayName = 'RichTextRenderer';
